import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import useAxios from "./useAxios";
import "./PublicCompetitionDraw.css";

const PublicCompetitionDraw = () => {
  const { id } = useParams();
  const [axios] = useAxios();
  
  const [competition, setCompetition] = useState(null);
  const [knockout, setKnockout] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.get(`public/competition/knockout/${id}`, (knockoutData) => {
          setKnockout(knockoutData);
          setCompetition(knockoutData.competition);
        });
      } catch (error) {
        console.error("Error fetching knockout data:", error);
      }
    };
    
    fetchData();
  }, [id]);

  if (!competition || !knockout) {
    return (
      <div className="public-draw-loading">
        <h1>Loading Tournament Draw...</h1>
      </div>
    );
  }

  // Calculate tournament structure
  const rounds = knockout.rounds || [];
  const maxRounds = rounds.length;

  // Helper functions to work with the complex nested match structure
  const getActualMatch = (knockoutMatch) => {
    return knockoutMatch.match;
  };

  const getHomePlayer = (knockoutMatch) => {
    if (knockoutMatch.match) {
      return knockoutMatch.match.home?.player;
    }
    // For future matches, try to get winner from home knockout match
    if (knockoutMatch.home?.match) {
      const homeMatch = knockoutMatch.home.match;
      if (homeMatch.status === 2) {
        return homeMatch.home?.winner ? homeMatch.home.player : homeMatch.away.player;
      }
    }
    return null;
  };

  const getAwayPlayer = (knockoutMatch) => {
    if (knockoutMatch.match) {
      return knockoutMatch.match.away?.player;
    }
    // For future matches, try to get winner from away knockout match
    if (knockoutMatch.away?.match) {
      const awayMatch = knockoutMatch.away.match;
      if (awayMatch.status === 2) {
        return awayMatch.home?.winner ? awayMatch.home.player : awayMatch.away.player;
      }
    }
    return null;
  };

  const getMatchResult = (knockoutMatch) => {
    const match = knockoutMatch.match;
    if (!match) return "";
    
    if (match.status === 2) { // Finished
      return `${match.home?.score || 0} - ${match.away?.score || 0}`;
    }
    
    if (match.status === 1) { // In Progress
      return `${match.home?.score || 0} - ${match.away?.score || 0} (Live)`;
    }
    
    return "";
  };

  const getWinner = (knockoutMatch) => {
    const match = knockoutMatch.match;
    if (!match || match.status !== 2) return null;
    
    if (match.home?.winner) {
      return match.home.player;
    } else if (match.away?.winner) {
      return match.away.player;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  document.title = `${competition.name} - Draw`;

  return (
    <div className="public-draw-container">
      <div className="public-draw-header">
        <h1>{competition.name}</h1>
        <div className="competition-info">
          <div className="info-item">
            <strong>Date:</strong> {formatDate(competition.dateTime)}
          </div>
          <div className="info-item">
            <strong>Type:</strong> {competition.type?.name || 'Knockout'}
          </div>
          {knockout.raceTo && (
            <div className="info-item">
              <strong>Race To:</strong> {knockout.raceTo}
            </div>
          )}
          <div className="info-item">
            <strong>Players:</strong> {knockout.players?.length || 0}
          </div>
        </div>
      </div>

      <div className="tournament-bracket">
        {rounds.map((round, roundIndex) => (
          <div key={roundIndex} className="tournament-round">
            <div className="round-header">
              <h3>
                {roundIndex === maxRounds - 1 ? 'Final' : 
                 roundIndex === maxRounds - 2 ? 'Semi-Final' : 
                 roundIndex === maxRounds - 3 ? 'Quarter-Final' : 
                 `Round ${roundIndex + 1}`}
              </h3>
            </div>
            
            <div className="round-matches">
              {round.matches?.map((knockoutMatch, matchIndex) => {
                const actualMatch = getActualMatch(knockoutMatch);
                const homePlayer = getHomePlayer(knockoutMatch);
                const awayPlayer = getAwayPlayer(knockoutMatch);
                const winner = getWinner(knockoutMatch);
                const result = getMatchResult(knockoutMatch);
                
                return (
                  <div key={matchIndex} className="match-card">
                    <div className="match-header">
                      <span className="match-number">Game {knockoutMatch.gameNumber}</span>
                      {(knockoutMatch.estimatedDateTime || actualMatch?.dateTime) && (
                        <span className="match-time">
                          {formatDate(knockoutMatch.estimatedDateTime || actualMatch?.dateTime)}
                        </span>
                      )}
                    </div>
                    
                    <div className="match-players">
                      <div className={`player ${winner && homePlayer && winner.id === homePlayer.id ? 'winner' : ''}`}>
                        <span className="player-name">
                          {homePlayer?.name || 'TBD'}
                        </span>
                        {homePlayer && (
                          <span className="player-handicap">({homePlayer.handicap})</span>
                        )}
                        {actualMatch?.status >= 1 && (
                          <span className="player-score">{actualMatch.home?.score || 0}</span>
                        )}
                      </div>
                      
                      <div className="vs-divider">vs</div>
                      
                      <div className={`player ${winner && awayPlayer && winner.id === awayPlayer.id ? 'winner' : ''}`}>
                        <span className="player-name">
                          {awayPlayer?.name || 'TBD'}
                        </span>
                        {awayPlayer && (
                          <span className="player-handicap">({awayPlayer.handicap})</span>
                        )}
                        {actualMatch?.status >= 1 && (
                          <span className="player-score">{actualMatch.away?.score || 0}</span>
                        )}
                      </div>
                    </div>
                    
                    {(knockoutMatch.table || actualMatch?.table) && (
                      <div className="match-table">
                        Table: {(knockoutMatch.table || actualMatch?.table)?.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublicCompetitionDraw;